import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Empty,
  Badge,
  Tooltip
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../../Utils/AppApi";
import {
  LIST_DEMO_ALLOWED_USERS,
  SAVE_DEMO_ALLOWED_USER,
  REMOVE_DEMO_ALLOWED_USER
} from "../../../Utils/appConstants";

const { Text, Title } = Typography;

const DemoAllowedUsersPage = ({ history }) => {
  const [username, setUsername] = useState("");
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const [loading, setLoading] = useState(false);        // list loader
  const [actionLoading, setActionLoading] = useState(false); // save/update loader
  const [removingId, setRemovingId] = useState(null);  // row delete loader

  /** INPUT */
  const handleChange = (e) => {
    setUsername(e.target.value.replace(/\s/g, "").toUpperCase());
  };

  /** FETCH USERS */
  const fetchUsers = useCallback(() => {
    setLoading(true);
    getRequest(LIST_DEMO_ALLOWED_USERS, history)
      .then(res => {
        if (res?.success) setUsers(res.data || []);
      })
      .finally(() => setLoading(false));
  }, [history]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /** SAVE / UPDATE */
  const handleSave = async () => {
    if (!username) return toast.error("Username required");

    setActionLoading(true);

    const payload = { username };
    if (editing) payload.id = editing._id;

    try {
      const res = await postRequest(SAVE_DEMO_ALLOWED_USER, payload, history);
      if (res?.success) {
        toast.success(res.message);
        setUsername("");
        setEditing(null);
        fetchUsers();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  /** REMOVE */
  const handleRemove = async (id) => {
    setRemovingId(id);
    try {
      const res = await postRequest(REMOVE_DEMO_ALLOWED_USER, { id }, history);
      if (res?.success) {
        toast.success("User removed");
        fetchUsers();
      } else {
        toast.error(res?.message);
      }
    } catch {
      toast.error("Remove failed");
    } finally {
      setRemovingId(null);
    }
  };

  /** TABLE COLUMNS */
  const columns = [
    {
      name: (
        <Space>
          <UserOutlined /> Username
        </Space>
      ),
      selector: row => row.username,
      sortable: true,
      cell: row => (
        <Space>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#667eea,#764ba2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 600
            }}
          >
            {row.username.charAt(0)}
          </div>
          <Text strong>{row.username}</Text>
        </Space>
      )
    },
    {
      name: "Status",
      center: true,
      cell: () => (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Allowed
        </Tag>
      )
    },
    {
      name: "Actions",
      right: true,
      cell: row => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              disabled={actionLoading}
              onClick={() => {
                setEditing(row);
                setUsername(row.username);
              }}
            />
          </Tooltip>

          <Popconfirm
            title="Remove this user?"
            onConfirm={() => handleRemove(row._id)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={removingId === row._id}
              disabled={actionLoading}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div
      style={{
        background: "#f8fafc",
        minHeight: "100vh",
        padding: 24
      }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <Row gutter={[32, 32]}>
          {/* LEFT PANEL */}
          <Col xs={24} lg={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 24px rgba(0,0,0,.06)"
              }}
              title={
                <Space>
                  <SafetyCertificateOutlined />
                  <Text strong>Demo Allowed User</Text>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input
                  placeholder="SRPAY1013"
                  value={username}
                  onChange={handleChange}
                  disabled={actionLoading}
                  style={{
                    height: 48,
                    borderRadius: 12,
                    fontWeight: 500
                  }}
                />

                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  loading={actionLoading}
                  onClick={handleSave}
                  block
                  style={{
                    height: 48,
                    borderRadius: 12,
                    background:
                      "linear-gradient(135deg,#667eea,#764ba2)",
                    border: "none"
                  }}
                >
                  {editing ? "Update User" : "Add User"}
                </Button>
              </Space>
            </Card>
          </Col>

          {/* RIGHT PANEL */}
          <Col xs={24} lg={16}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 24px rgba(0,0,0,.06)"
              }}
            >
              <Space
                style={{
                  marginBottom: 16,
                  justifyContent: "space-between",
                  width: "100%"
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  Allowed Users
                </Title>
                <Badge count={users.length} />
              </Space>

              <DataTable
                columns={columns}
                data={users}
                progressPending={loading}
                pagination
                highlightOnHover
                noDataComponent={<Empty />}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DemoAllowedUsersPage;
